<template>
    <div v-class="'form-control'">
        <i class="fa fa-calendar"></i>&nbsp;
        <span key="placeholder" v-if="! startDate || ! endDate" class="placeholder">{{placeholder}}</span>
        <span key="rangeString" v-else>{{rangeString}}</span>
    </div>
</template>
<script>
export default {
    name: 'oc-datetime-range-input',
    model: {
        prop: 'rangeString',
        event: 'input'
    },
    watch: {
        startDate: {
            handler(newVal) {
                if( newVal !== undefined && newVal !== '' ) {
                    this.picker.setStartDate(newVal)
                }
            }
        },
        endDate: {
            handler(newVal) {
                if( newVal !== undefined && newVal !== '' ) {
                    this.picker.setEndDate(newVal)
                }
            }
        }
    },
    props: {
        /**
         * The maximum span between the selected start and end dates.
         * Check off maxSpan in the configuration generator for an example of how to use this.
         * You can provide any object the moment library would let you add to a date.
         */
        maxSpan: {
            type: [String, Date, Boolean],
            default: false
        },

        /**
         * The beginning date of the initially selected date range. If you provide a string,
         * it must match the date format string set in your locale setting.
         */
        startDate: {
            type: [String, Date],
            default: null
        },

        /**
         * The end date of the initially selected date range.
         */
        endDate: {
            type: [String, Date],
            default: null
        },

        /**
         * Set predefined date ranges the user can select from.
         * Each key is the label for the range, and its value an array with two dates representing the bounds of the range.
         * Boolean true - means to use the default one.
         */
        ranges: {
            type: [Function, Object, Boolean],
            default: true
        },

        /* ------------------------------------------------------------------ */

        /**
         * The earliest date a user may select.
         */
        minDate: {
            type: [String, Date, Boolean],
            default: false
        },

        /**
         * The latest date a user may select.
         */
        maxDate: {
            type: [String, Date, Boolean],
            default: false
        },

        /**
         * Show the week numbers on the left side of the calendar.
         */
        showWeekNumbers: {
            type: Boolean,
            default: false
        },

        /**
         * Show ISO week numbers at the start of each week on the calendars.
         */
        showISOWeekNumbers: {
            type: Boolean,
            default: false
        },

        /**
         * Show year and month select boxes above calendars to jump to a specific month and year.
         */
        showDropdowns: {
            type: Boolean,
            default: false
        },

        /**
         * Show the dropdowns for time (hour/minute) selection below the calendars.
         */
        timePicker: {
            type: Boolean,
            default: true
        },

        /**
         * Increment of the minutes selection list for times (i.e. 30 to allow only selection of times ending in 0 or 30).
         */
        timePickerIncrement: {
            type: Number,
            default: 1
        },

        /**
         * Use 24-hour instead of 12-hour times, removing the AM/PM selection.
         */
        timePicker24Hour: {
            type: Boolean,
            default: true
        },

        /**
         * Show seconds in the timePicker.
         */
        timePickerSeconds: {
            type: Boolean,
            default: false
        },

        /**
         * Hide the apply and cancel buttons, and automatically apply a new date range as soon as two dates are clicked.
         */
        autoApply: {
            type: Boolean,
            default: false
        },

        /* ------------------------------------------------------------------ */

        /**
         * Default placeholder
         */
        placeholder: {
            type: String,
            default: '(not set)'
        },

        /**
         * <oc-text-input> attributes
         */
        inputAttrs: {
            type: Object,
            default: () => ({})
        },

        /**
         * Date format.
         */
        format: {
            type: String,
            default() {
                if( this.timePicker === false ) {
                    return 'YYYY-MM-DD'
                }

                if( this.timePickerSeconds === false ) {
                    return 'YYYY-MM-DD HH:mm'
                }

                return 'YYYY-MM-DD HH:mm:ss'
            }
        },

        /**
         * The timeZone that will be used in moment()
         */
        timeZone: {
            type: String,
            default() {
                if( window.moment !== undefined && window.moment.tz !== undefined ) {
                    return moment().tz() // current timezone
                }

                return null
            }
        },

        /**
         * Disabled state. If true picker do not popup on click.
         */
        disabled: {
            type: Boolean,
            default: false
        },

        /**
         * Custom options.
         * https://www.daterangepicker.com/#config
         *
         * @see _defaultOptions()
         */
        options: {
            type: Object,
            default() {
                return {}
            }
        },

        /**
         * The default datetime range that will be loaded on init.
         * Format: 'Today' OR {startDate: '', endDate: ''}
         */
        defaultRange: {
            type: [String, Object],
            default: null
        },

        /**
         * Whether to apply the default value on startup or not.
         */
        applyOnStartup: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            picker: undefined
        }
    },
    mounted() {
        this.init()
    },
    methods: {
        emitState() {
            let startDate = this.picker.startDate.format(this.format)
            let endDate = this.picker.endDate.format(this.format)

            this.$emit('update:startDate', startDate)
            this.$emit('update:endDate', endDate)

            setTimeout(() => {
                // this triggers validation and allows for v-model
                this.$emit('input', startDate + this.$_options.locale.separator + endDate)
            }, 0)
        },
        reload() {
            this.picker.setStartDate(this.startDate)
            this.picker.setEndDate(this.endDate)
        },
        show() {
            this.picker.show()
        },
        hide() {
            this.picker.hide()
        },
        toggle() {
            this.picker.toggle()
        },
        init()
        {
            this.$nextTick(() => {
                $(this.$el).daterangepicker(this.$_options)

                this.picker = $(this.$el).data('daterangepicker')

                this._registerEvents()

                this.$emit('ready', this.picker)

                if( this.applyOnStartup ) {
                    this.emitState()
                }
            })
        },
        _getDateRangers() {
            return  {
                'Today': [this.moment().startOf('day'), this.moment().endOf('day')],
                'Last 3 Days': [this.moment().subtract(2, 'days').startOf('day'), this.moment().endOf('day')],
                'Last 7 Days': [this.moment().subtract(6, 'days').startOf('day'), this.moment().endOf('day')],
                'Last 14 Days': [this.moment().subtract(13, 'days').startOf('day'), this.moment().endOf('day')],
                'Last 30 Days': [this.moment().subtract(29, 'days').startOf('day'), this.moment().endOf('day')],
                'This Month': [this.moment().startOf('month').startOf('day'), this.moment().endOf('month').endOf('day')],
                'Last Month': [this.moment().subtract(1, 'month').startOf('month').startOf('day'), this.moment().subtract(1, 'month').endOf('month').endOf('day')]
            }
        },

        _defaultOptions() {
            return {
                // ('left'/'right'/'center') Whether the picker appears aligned to the left, to the right, or centered under the HTML element it's attached to.
                opens: 'right',
                // ('down'/'up'/'auto') Whether the picker appears below (default) or above the HTML element it's attached to.
                drops: 'down',
                // Allows you to provide localized strings for buttons and labels, customize the date format, and
                // change the first day of week for the calendars.
                // Check off locale in the configuration generator to see how to customize these options.
                locale: {
                    direction: 'ltr',
                    format: this.format,
                    separator: ' - ',
                    applyLabel: 'Apply',
                    cancelLabel: 'Cancel',
                    weekLabel: 'W',
                    customRangeLabel: 'Custom Range',
                    daysOfWeek: moment.weekdaysMin(),
                    monthNames: moment.monthsShort(),
                    firstDay: moment.localeData().firstDayOfWeek()
                },
                // Normally, if you use the ranges option to specify pre-defined date ranges, calendars
                // for choosing a custom date range are not shown until the user clicks "Custom Range".
                // When this option is set to true, the calendars for choosing a custom date range are always shown instead.
                alwaysShowCalendars: true,
                // When enabled, the two calendars displayed will always be for two sequential months
                // (i.e. January and February), and both will be advanced when clicking the left or right arrows above the calendars.
                // When disabled, the two calendars can be individually advanced and display any month/year.
                linkedCalendars: false,
                // Show only a single calendar to choose one date, instead of a range picker with two calendars.
                // The start and end dates provided to your callback will be the same single date chosen.
                singleDatePicker: false,
                // A function that is passed each date in the two calendars before they are displayed, and
                // may return true or false to indicate whether that date should be available for selection or not.
                isInvalidDate: () => false,
                // A function that is passed each date in the two calendars before they are displayed, and
                // may return a string or array of CSS class names to apply to that date's calendar cell.
                isCustomDate: () => false,
                // Indicates whether the date range picker should automatically update the value
                // of the <input> element it's attached to at initialization and when the selected dates change.
                autoUpdateInput: false,
                /**
                 * Displays "Custom Range" at the end of the list of predefined ranges, when the ranges option is used.
                 * This option will be highlighted whenever the current date range selection does not match one of the predefined ranges.
                 * Clicking it will display the calendars to select a new custom range.
                 */
                showCustomRangeLabel: false,
                // CSS class names that will be added to both the apply and cancel buttons.
                buttonClasses: 'btn btn-sm',
                // CSS class names that will be added only to the apply button.
                applyButtonClasses: 'btn-primary',
                // CSS class names that will be added only to the cancel button.
                cancelButtonClasses: 'btn-default',
                // jQuery selector of the parent element that the date range picker will be added to, if not provided this will be 'body'.
                parentEl: ''
            }
        },

        _registerEvents() {
            const el = $(this.$el)

            // triggered when the picker is shown.
            el.on('show.daterangepicker', (event, picker) => {
                this.$emit('show', event, picker)
            })

            // triggered when the picker is hidden.
            el.on('hide.daterangepicker', (event, picker) => {
                this.$emit('hide', event, picker)
            })

            // triggered when the calendar(s) are shown.
            el.on('showCalendar.daterangepicker', (event, picker) => {
                this.$emit('show-calendar', event, picker)
            })

            // triggered when the calendar(s) are hidden.
            el.on('hideCalendar.daterangepicker', (event, picker) => {
                this.$emit('hide-calendar', event, picker)
            })

            // triggered when the apply button is clicked, or when a predefined range is clicked.
            el.on('apply.daterangepicker', (event, picker) => {
                this.$emit('apply', event, picker)

                this.emitState()
            })

            // triggered when the cancel button is clicked.
            el.on('cancel.daterangepicker', (event, picker) => {
                this.$emit('cancel', event, picker)
            })
        },
        _unRegisterEvents() {
            this.$off()
        },
        moment() {
            if( window.moment !== undefined && window.moment.tz !== undefined ) {
                return moment().tz(this.timeZone)
            }

            return moment()
        }
    },
    computed: {
        rangeString() {
            return this.startDate + this.$_options.locale.separator + this.endDate
        },
        $_inputAttrs() {
            return this.inputAttrs
        },

        $_options() {

            ////////////////////////////////////////////////////////

            let ranges = {}

            let rangesProp = typeOf(this.ranges) === 'function'
                            ? this.ranges()
                            : this.ranges

            if( rangesProp === true ) {
                ranges = this._getDateRangers()
            } else {
                ranges = rangesProp
            }

            ////////////////////////////////////////////////////////

            let startDate = this.startDate
            let endDate = this.endDate

            if( this.defaultRange !== null && ranges[this.defaultRange] && ! startDate && ! endDate )
            {
                startDate = ranges[this.defaultRange][0]
                endDate = ranges[this.defaultRange][1]
            }

            if( ! startDate ) {
                startDate = this.moment().startOf('day')
            }

            if( ! endDate ) {
                endDate = this.moment().endOf('day')
            }

            ////////////////////////////////////////////////////////

            let defaultOptions = this._defaultOptions()

            let locale = Object.assign({}, defaultOptions.locale)
            if( this.options.locale !== undefined ) {
                locale = Object.assign({}, locale, this.options.locale)
            }

            return Object.assign(
                {},
                defaultOptions,
                this.options,
                {locale},
                {
                    ranges,
                	startDate,
                	endDate,
                    maxSpan: this.maxSpan,
                    minDate: this.minDate,
                    maxDate: this.maxDate,
                    showWeekNumbers: this.showWeekNumbers,
                    showISOWeekNumbers: this.showISOWeekNumbers,
                    showDropdowns: this.showDropdowns,
                    timePicker: this.timePicker,
                    timePickerIncrement: this.timePickerIncrement,
                    timePicker24Hour: this.timePicker24Hour,
                    timePickerSeconds: this.timePickerSeconds,
                    autoApply: this.autoApply
                }
            )
        }
    },
    beforeDestroy() {
        this._unRegisterEvents()

        $(this.$el)
            .daterangepicker("hide")
            .daterangepicker("destroy")
    }
}
</script>
<style scoped>
.placeholder {
    color: #6c757d;
}
</style>
